import { StyledListingsApplications, StatusCell } from './style';
import { Button, Table, Dropdown, Input, DatePicker, Tag } from 'ui';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { permissions } from 'config/config';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { MenuProps, message, Modal } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import useQueryApiClient from 'utils/useQueryApiClient';
import Filter from '../../Filter';
import { Pagination } from 'types/Pagination';
import EnumSelect from 'admin/Selects/EnumSelect';
import type { ListingApplication } from 'types/ListingData';
import dayjs from 'dayjs';
import { DateProgress } from 'ui/DateProgress';
import { DrawerWithdrawListingApplication } from 'ui/Drawer/Drawers/DrawerWithdrawListingApplication';
import { DrawerViewListingApplication } from 'ui/Drawer/Drawers/DrawerViewListingApplication';
import DrawerSendMessage from 'ui/Drawer/Drawers/DrawerSendMessage';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import { MobileListField } from 'ui/Card/MobileListCard';
import { publicPath } from 'enums/publicRoutes';

const EDITABLE_STATUSES = ['new', 'rejected'];

interface ListingsApplicationsProps {
  reload?: number;
  onEdit?: (applicationId: number) => void;
  filter?: Record<string, unknown>;
  onFilterChange?: (filter: Record<string, unknown>) => void;
  onPaginationChange?: (status: { total?: number }) => void;
  filterContent?: React.ReactNode;
}

const defaultFilterParams = { page_size: 10 };

const ListingsApplications = ({
  reload = 0,
  onEdit,
  filter,
  onFilterChange,
  onPaginationChange,
  filterContent,
}: ListingsApplicationsProps) => {
  const { can } = useAuth();
  const canEdit = can(permissions.edit_farm_listing);
  const [internalFilter, setInternalFilter] =
    useState<Record<string, unknown>>(defaultFilterParams);
  const advancedSearch = filter ?? internalFilter;
  const setAdvancedSearch = onFilterChange ?? setInternalFilter;
  const [paginationStatus, setPaginationStatus] = useState<Pagination>({
    total: 0,
    page: 1,
    page_size: 10,
  });
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isWithdrawDrawerOpen, setIsWithdrawDrawerOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewedApplication, setViewedApplication] =
    useState<ListingApplication | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const [messageContext, setMessageContext] = useState<{
    listingId?: string;
    preselectedRecipients?: number[];
  } | null>(null);
  const intl = useIntl();
  const navigate = useNavigate();

  const isEditableStatus = useCallback((status: string | undefined) => {
    return status && EDITABLE_STATUSES.includes(status);
  }, []);

  const isWithDrawable = useCallback((status: string | undefined) => {
    return !!status && ['new', 'submitted', 'selected'].includes(status);
  }, []);

  const handleWithdraw = useCallback((applicationId: number) => {
    setSelectedApplicationId(applicationId);
    setIsWithdrawDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (reload > 0) {
      setReloadTrigger((prev) => prev + 1);
    }
  }, [reload]);

  const { appendData: deleteApplication } = useQueryApiClient({
    request: {
      url: `/api/v2/listing-applications/:id`,
      method: 'DELETE',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({ id: 'message.successfully_deleted_application' })
      );
      setReloadTrigger((prev) => prev + 1);
    },
    onError: (response) => {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    },
  });

  const { appendData: performAction, isLoading: isActionLoading } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listing-applications/:id/action`,
        method: 'POST',
      },
      onSuccess: (_response, passOnSuccess) => {
        const actionType = passOnSuccess?.action as string;
        const messageKey =
          actionType === 'accept'
            ? 'message.application_accepted'
            : actionType === 'withdraw'
              ? 'message.application_withdrawn'
              : actionType === 'reject'
                ? 'message.application_rejected'
                : 'message.application_selected';

        message.success(intl.formatMessage({ id: messageKey }));
        setReloadTrigger((prev) => prev + 1);
      },
      onError: (error) => {
        const errorMessage =
          error?.message ||
          error?.errors?.action?.[0] ||
          intl.formatMessage({ id: 'message.error' });
        message.error(errorMessage);
      },
      onFinally: () => setPendingAction(null),
    });

  const handleAccept = useCallback(
    (applicationId: number) => {
      setPendingAction('accept');
      performAction(
        { action: 'accept' },
        { id: applicationId },
        { action: 'accept' }
      );
    },
    [performAction]
  );

  const handleDelete = (applicationId: number): void => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'message.confirm_delete_application' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => {
        deleteApplication(null, { id: applicationId });
      },
    });
  };

  const onRow = useCallback(
    (record: ListingApplication) => ({
      onClick: () => {
        if (!canEdit || !isEditableStatus(record.status)) {
          setViewedApplication(record);
          setViewDrawerOpen(true);
          return;
        }

        onEdit?.(record.id);
      },
      style: { cursor: 'pointer' },
    }),
    [canEdit, isEditableStatus, onEdit]
  );

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'listings.title',
        }),
        dataIndex: 'listing_title',
        render: (value: string) => value || '',
      },
      {
        title: intl.formatMessage({ id: 'listings.applications.submitted_at' }),
        dataIndex: 'submitted_at',
        render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.submission_deadline',
        }),
        dataIndex: 'listing_publication_end_date',
        render: (value: string, record: ListingApplication) => {
          if (!value) return '-';

          const endDate = dayjs(value);
          const isExpired = dayjs().isAfter(endDate);

          if (!record.listing_publication_start_date) {
            return isExpired
              ? intl.formatMessage({ id: 'listings.applications.ended' })
              : endDate.format('DD.MM.YYYY - HH:mm');
          }

          return (
            <div className="deadline-progress">
              <div className="deadline-date">
                {isExpired
                  ? intl.formatMessage({ id: 'listings.applications.ended' })
                  : endDate.format('DD.MM.YYYY - HH:mm')}
              </div>
              <DateProgress
                startDate={record.listing_publication_start_date}
                endDate={value}
              />
            </div>
          );
        },
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.listing_number',
        }),
        dataIndex: 'listing_identifier',
        render: (value: string) => value || '',
      },
      {
        title: intl.formatMessage({
          id: 'listings.applications.transaction_type',
        }),
        dataIndex: 'listing_transaction_type',
        render: (value: string) =>
          value
            ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` })
            : '-',
      },
      {
        title: intl.formatMessage({ id: 'listings.applications.offer_amount' }),
        dataIndex: 'total_offer_price',
        render: (value: number | string | null) => {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return numValue && !isNaN(numValue) ? `${numValue}` : '';
        },
      },
      {
        title: intl.formatMessage({ id: 'listings.applications.status' }),
        dataIndex: 'status',
        render: (value: string) => (
          <StatusCell>
            <Tag className={`status ${value}`}>
              {intl.formatMessage({ id: `listings.applications.${value}` })}
            </Tag>
          </StatusCell>
        ),
      },
      {
        title: (
          <div className={'actions-title'}>
            {intl.formatMessage({ id: 'listings.applications.actions' })}
          </div>
        ),
        render: (record: ListingApplication) => {
          const getMenuItems = (): MenuProps['items'] => {
            const items: MenuProps['items'] = [];

            if (canEdit && isEditableStatus(record.status)) {
              items.push({
                label: intl.formatMessage({ id: 'listings.applications.edit' }),
                key: 'edit',
              });
            }

            if (canEdit && isWithDrawable(record.status)) {
              items.push({
                label: intl.formatMessage({
                  id: 'listings.applications.withdraw.action',
                }),
                key: 'withdraw',
                danger: true,
              });
            }

            items.push({
              label: intl.formatMessage({ id: 'listings.message' }),
              key: 'message',
            });

            if (record.status === 'selected' && canEdit) {
              items.push({
                label: intl.formatMessage({
                  id: 'listings.applications.accept_offer',
                }),
                key: 'accept',
                disabled: isActionLoading && pendingAction === 'accept',
              });
            }

            items.push({
              label: intl.formatMessage({
                id: 'listings.applications.view_listing',
              }),
              key: 'view_listing',
            });

            return items;
          };

          const menuItems = getMenuItems();

          return menuItems && menuItems.length > 0 ? (
            <Dropdown
              menu={{
                items: menuItems,
                onClick: (value) => {
                  value.domEvent?.stopPropagation?.();
                  switch (value.key) {
                    case 'edit':
                      onEdit?.(record.id);
                      break;
                    case 'withdraw':
                      handleWithdraw(record.id);
                      break;
                    case 'accept':
                      handleAccept(record.id);
                      break;
                    case 'message':
                      setMessageContext({ listingId: record.listing_id });
                      setMessageDrawerOpen(true);
                      break;
                    case 'view_listing':
                      navigate(publicPath.wholesaleListingDetail(record.listing_id));
                      break;
                    case 'delete':
                      handleDelete(record.id);
                      break;
                  }
                },
              }}
            >
              <Button
                className="action"
                label={intl.formatMessage({
                  id: 'listings.applications.actions',
                })}
                icon={<DownOutlined />}
                iconPosition="end"
                size="middle"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            </Dropdown>
          ) : null;
        },
      },
    ],
    [
      intl,
      canEdit,
      isEditableStatus,
      isWithDrawable,
      onEdit,
      handleWithdraw,
      handleAccept,
      isActionLoading,
      pendingAction,
    ]
  );

  const mobileItems: {
    coverFields: MobileListField[];
    bodyFields: MobileListField[];
    footerFields: MobileListField[];
  } = useMemo(
    () => ({
      coverFields: [
        {
          name: 'listing_title',
          render: (value: string) => (
            <div className="mobile-list-card-cover-title">
              <span className="label">
                {intl.formatMessage({ id: 'listings.title' })}
              </span>
              <span className="value">{value || '-'}</span>
            </div>
          ),
        },
      ],
      bodyFields: [
        {
          name: 'submitted_at',
          label: intl.formatMessage({
            id: 'listings.applications.submitted_at',
          }),
          render: (value: string) =>
            value ? dayjs(value).format('DD.MM.YYYY') : '-',
        },
        {
          name: 'listing_publication_end_date',
          label: intl.formatMessage({
            id: 'listings.applications.submission_deadline',
          }),
          render: (value: string, record: ListingApplication) => {
            if (!value) return '-';
            const endDate = dayjs(value);
            const isExpired = dayjs().isAfter(endDate);
            return isExpired
              ? intl.formatMessage({ id: 'listings.applications.ended' })
              : endDate.format('DD.MM.YYYY - HH:mm');
          },
        },
        {
          name: 'listing_transaction_type',
          label: intl.formatMessage({
            id: 'listings.applications.transaction_type',
          }),
          render: (value: string) =>
            value
              ? intl.formatMessage({ id: `enum.${value.toUpperCase()}` })
              : '-',
        },
        {
          name: 'listing_identifier',
          label: intl.formatMessage({
            id: 'listings.applications.listing_number',
          }),
          render: (value: string) => value || '-',
        },
        {
          name: 'total_offer_price',
          label: intl.formatMessage({
            id: 'listings.applications.offer_amount',
          }),
          render: (value: number | string | null) => {
            const numValue =
              typeof value === 'string' ? parseFloat(value) : value;
            return numValue && !isNaN(numValue) ? `${numValue}` : '-';
          },
        },
        {
          name: 'status',
          label: intl.formatMessage({ id: 'listings.applications.status' }),
          render: (value: string) => (
            <StatusCell>
              <Tag className={`status ${value}`}>
                {intl.formatMessage({ id: `listings.applications.${value}` })}
              </Tag>
            </StatusCell>
          ),
        },
      ],
      footerFields: [
        {
          name: 'actions',
          render: (_: unknown, record: ListingApplication) => {
            const getMenuItems = (): MenuProps['items'] => {
              const items: MenuProps['items'] = [];

              if (canEdit && isEditableStatus(record.status)) {
                items.push({
                  label: intl.formatMessage({
                    id: 'listings.applications.edit',
                  }),
                  key: 'edit',
                });
              }

              if (canEdit && isWithDrawable(record.status)) {
                items.push({
                  label: intl.formatMessage({
                    id: 'listings.applications.withdraw.action',
                  }),
                  key: 'withdraw',
                  danger: true,
                });
              }

              items.push({
                label: intl.formatMessage({ id: 'listings.message' }),
                key: 'message',
              });

              if (record.status === 'selected' && canEdit) {
                items.push({
                  label: intl.formatMessage({
                    id: 'listings.applications.accept_offer',
                  }),
                  key: 'accept',
                  disabled: isActionLoading && pendingAction === 'accept',
                });
              }

              items.push({
                label: intl.formatMessage({
                  id: 'listings.applications.view_listing',
                }),
                key: 'view_listing',
              });

              return items;
            };

            const menuItems = getMenuItems();

            return menuItems && menuItems.length > 0 ? (
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown
                  menu={{
                    items: menuItems,
                    onClick: (value) => {
                      value.domEvent?.stopPropagation?.();
                      switch (value.key) {
                        case 'edit':
                          onEdit?.(record.id);
                          break;
                        case 'withdraw':
                          handleWithdraw(record.id);
                          break;
                        case 'accept':
                          handleAccept(record.id);
                          break;
                        case 'message':
                          setMessageContext({ listingId: record.listing_id });
                          setMessageDrawerOpen(true);
                          break;
                        case 'view_listing':
                          navigate(publicPath.wholesaleListingDetail(record.listing_id));
                          break;
                      }
                    },
                  }}
                >
                  <Button
                    className="action"
                    label={intl.formatMessage({
                      id: 'listings.applications.actions',
                    })}
                    icon={<DownOutlined />}
                    iconPosition="end"
                    size="small"
                  />
                </Dropdown>
              </div>
            ) : null;
          },
        },
      ],
    }),
    [
      intl,
      canEdit,
      isEditableStatus,
      isWithDrawable,
      onEdit,
      handleWithdraw,
      handleAccept,
      isActionLoading,
      pendingAction,
    ]
  );

  return (
    <StyledListingsApplications>
      <ResponsiveWrapper
        desktopComponent={
          <>
            {filterContent ?? (
              <Filter
                onFinish={setAdvancedSearch}
                totalRecord={paginationStatus?.total ?? 0}
                isLabelVisible={true}
                defaultFilterParam={defaultFilterParams}
              >
                <Input
                  label={intl.formatMessage({
                    id: 'listings.applications.search',
                  })}
                  name="search"
                  allowClear
                />
                <EnumSelect
                  label={intl.formatMessage({
                    id: 'listings.filter_by_status',
                  })}
                  name="status"
                  mode="multiple"
                  code="ListingApplicationStatus"
                />
                <EnumSelect
                  label={intl.formatMessage({ id: 'listings.filter_by_type' })}
                  name="transaction_type"
                  mode="multiple"
                  code="ListingTransactionType"
                />
                <DatePicker
                  label={intl.formatMessage({
                    id: 'listings.filter_by_submitted_until',
                  })}
                  name="submitted_at"
                  disabledDate={(current) => {
                    if (!current) return false;
                    return current.isBefore(dayjs().startOf('day'), 'day');
                  }}
                />
                <Button
                  type="primary"
                  label={intl.formatMessage({
                    id: 'listings.apply_filter',
                  })}
                  htmlType="submit"
                />
              </Filter>
            )}
            <Table
              url={`/api/v2/listing-applications`}
              columns={columns}
              filter={advancedSearch}
              rowKey="id"
              reload={reloadTrigger}
              paginnationStatus={(status) => {
                setPaginationStatus(status);
                onPaginationChange?.(status as { total?: number });
              }}
              onRow={onRow}
              rowClassName="is-clickable"
            />
          </>
        }
        mobileComponent={
          <MobileList
            url="/api/v2/listing-applications"
            filter={advancedSearch}
            reload={reloadTrigger}
            rowKey="id"
            coverFields={mobileItems.coverFields}
            bodyFields={mobileItems.bodyFields}
            footerFields={mobileItems.footerFields}
            paginnationStatus={(status) => {
              setPaginationStatus(status);
              onPaginationChange?.(status as { total?: number });
            }}
            page_size={advancedSearch.page_size as number}
            onItemClick={(record: ListingApplication) => {
              if (!canEdit || !isEditableStatus(record.status)) {
                setViewedApplication(record);
                setViewDrawerOpen(true);
                return;
              }
              onEdit?.(record.id);
            }}
          />
        }
      />
      <DrawerWithdrawListingApplication
        open={isWithdrawDrawerOpen}
        onClose={() => {
          setIsWithdrawDrawerOpen(false);
          setSelectedApplicationId(null);
          setReloadTrigger((old) => old + 1);
        }}
        applicationId={selectedApplicationId || 0}
      />
      <DrawerViewListingApplication
        open={viewDrawerOpen}
        onClose={() => {
          setViewDrawerOpen(false);
          setViewedApplication(null);
        }}
        application={viewedApplication}
        onActionCompleted={() => {
          setReloadTrigger((old) => old + 1);
        }}
      />
      <DrawerSendMessage
        open={messageDrawerOpen}
        onClose={() => {
          setMessageDrawerOpen(false);
          setMessageContext(null);
        }}
        context={
          messageContext
            ? {
                type: 'listing',
                listingId: messageContext.listingId,
                preselectedRecipients: messageContext.preselectedRecipients,
              }
            : undefined
        }
      />
    </StyledListingsApplications>
  );
};

export default ListingsApplications;
